/**
 * Schedule create/edit modal — cadence builder (weekly / monthly incl. last
 * day), 5-minute ET time select, run-mode radio with the clean-gate policy
 * shown for auto_live, notify emails, and a "next 5 fires" preview computed
 * by the SAME scheduleMath module the dispatcher uses (bundled directly from
 * functions/, like engineClient — one math, one code path).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { payrollApi } from '../api';
import scheduleMath from '../../../../functions/Payroll/schedule/scheduleMath';
import { FLAG_VOCABULARY } from '../engineClient';

const { nextOccurrences } = scheduleMath;

const DEFAULT_HOLD_FLAGS = ['MISSING_CORE_DATA', 'SKIPPED_NO_PROFILE', 'UNMATCHED_LO'];
// Base flag names from the engine vocabulary ('UNMATCHED_LO:email' → 'UNMATCHED_LO'),
// defaults first so the checklist reads naturally.
const HOLDABLE_FLAGS = [...new Set([
  ...DEFAULT_HOLD_FLAGS,
  ...Object.keys(FLAG_VOCABULARY).map((k) => k.split(':')[0]),
])];

const DOW_OPTIONS = [
  { v: 1, label: 'Monday' }, { v: 2, label: 'Tuesday' }, { v: 3, label: 'Wednesday' },
  { v: 4, label: 'Thursday' }, { v: 5, label: 'Friday' }, { v: 6, label: 'Saturday' },
  { v: 0, label: 'Sunday' },
];

const timeLabel = (hh, mm) => {
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, '0')} ${hh < 12 ? 'AM' : 'PM'}`;
};
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h += 1) {
  for (let m = 0; m < 60; m += 5) {
    TIME_OPTIONS.push({ v: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, label: timeLabel(h, m) });
  }
}

const fmtFire = (d) => d.toLocaleString('en-US', {
  timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric',
  year: 'numeric', hour: 'numeric', minute: '2-digit',
});

const defaultForm = () => ({
  label: '',
  cadence: 'weekly',
  day_of_week: 5,
  day_of_month: -1,
  time_of_day: '18:00',
  run_mode: 'dry_only',
  hold_flags: [...DEFAULT_HOLD_FLAGS],
  hold_on_blank_payee: true,
  max_total: '',
  pause_on_failure: true,
  notify_emails: 'mitchellm@future.loans',
  notes: '',
  enabled: true,
});

const fromSchedule = (s) => ({
  label: s.label || '',
  cadence: s.cadence || 'weekly',
  day_of_week: s.day_of_week == null ? 5 : s.day_of_week,
  day_of_month: s.day_of_month == null ? -1 : s.day_of_month,
  time_of_day: s.time_of_day || '18:00',
  run_mode: s.run_mode || 'dry_only',
  hold_flags: Array.isArray(s.hold_flags) ? s.hold_flags : [...DEFAULT_HOLD_FLAGS],
  hold_on_blank_payee: s.hold_on_blank_payee !== false,
  max_total: s.max_total == null ? '' : String(s.max_total),
  pause_on_failure: s.pause_on_failure !== false,
  notify_emails: (s.notify_emails || []).join(', '),
  notes: s.notes || '',
  enabled: s.enabled !== false,
});

export default function ScheduleModal({ isOpen, onClose, schedule, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(schedule && schedule.id);

  useEffect(() => {
    if (isOpen) setForm(schedule && schedule.id ? fromSchedule(schedule) : defaultForm());
  }, [isOpen, schedule]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const preview = useMemo(() => {
    try {
      return nextOccurrences({
        cadence: form.cadence,
        day_of_week: Number(form.day_of_week),
        day_of_month: Number(form.day_of_month),
        time_of_day: form.time_of_day,
      }, 5);
    } catch (e) {
      return [];
    }
  }, [form.cadence, form.day_of_week, form.day_of_month, form.time_of_day]);

  const emails = form.notify_emails.split(',').map((e) => e.trim()).filter(Boolean);

  const save = async () => {
    if (!form.label.trim()) {
      toast({ title: 'Give the schedule a label', status: 'warning', duration: 4000 });
      return;
    }
    if (emails.length === 0) {
      toast({ title: 'At least one notify email is required', status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        enabled: form.enabled,
        cadence: form.cadence,
        day_of_week: form.cadence === 'weekly' ? Number(form.day_of_week) : null,
        day_of_month: form.cadence === 'monthly' ? Number(form.day_of_month) : null,
        time_of_day: form.time_of_day,
        run_mode: form.run_mode,
        hold_flags: form.hold_flags,
        hold_on_blank_payee: form.hold_on_blank_payee,
        max_total: form.max_total === '' ? null : Number(form.max_total),
        pause_on_failure: form.pause_on_failure,
        notify_emails: emails,
        notes: form.notes.trim() || null,
      };
      await payrollApi.manageSchedules(
        isEdit
          ? { action: 'update', id: schedule.id, payload }
          : { action: 'create', payload },
      );
      toast({ title: isEdit ? 'Schedule updated' : 'Schedule created', status: 'success', duration: 4000 });
      await onSaved();
      onClose();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: null, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit schedule' : 'New schedule'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Label</FormLabel>
              <Input size="sm" value={form.label} placeholder="e.g. Friday end-of-business payroll"
                onChange={(e) => set('label', e.target.value)} />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Cadence (all times ET)</FormLabel>
              <RadioGroup value={form.cadence} onChange={(v) => set('cadence', v)}>
                <Stack direction="row" spacing={6}>
                  <Radio value="weekly">Weekly</Radio>
                  <Radio value="monthly">Monthly</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            <Flex gap={3} wrap="wrap">
              {form.cadence === 'weekly' ? (
                <FormControl maxW="220px">
                  <FormLabel fontSize="sm">Day of week</FormLabel>
                  <Select size="sm" value={form.day_of_week} onChange={(e) => set('day_of_week', e.target.value)}>
                    {DOW_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </Select>
                </FormControl>
              ) : (
                <FormControl maxW="220px">
                  <FormLabel fontSize="sm">Day of month</FormLabel>
                  <Select size="sm" value={form.day_of_month} onChange={(e) => set('day_of_month', e.target.value)}>
                    <option value={-1}>Last day of the month</option>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>Day {d}</option>
                    ))}
                  </Select>
                  <FormHelperText fontSize="xs">Days 29–31 aren&apos;t offered — &quot;last day&quot; covers month ends.</FormHelperText>
                </FormControl>
              )}
              <FormControl maxW="180px">
                <FormLabel fontSize="sm">Time (ET)</FormLabel>
                <Select size="sm" value={form.time_of_day} onChange={(e) => set('time_of_day', e.target.value)}>
                  {TIME_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </Select>
              </FormControl>
            </Flex>

            <Box borderWidth="1px" borderRadius="md" p={3} fontSize="sm">
              <Text fontWeight="semibold" mb={1}>Next 5 fires</Text>
              {preview.length === 0 && <Text color="gray.500">—</Text>}
              {preview.map((d) => <Text key={d.toISOString()} fontSize="xs">{fmtFire(d)} ET</Text>)}
            </Box>

            <Divider />

            <FormControl>
              <FormLabel fontSize="sm">Run mode</FormLabel>
              <RadioGroup value={form.run_mode} onChange={(v) => set('run_mode', v)}>
                <Stack spacing={2}>
                  <Radio value="dry_only">
                    <Badge colorScheme="teal" mr={2}>dry_only</Badge>
                    <Text as="span" fontSize="sm">Compute + email a report. Nothing is written to the sheets.</Text>
                  </Radio>
                  <Radio value="auto_live">
                    <Badge colorScheme="orange" mr={2}>auto_live</Badge>
                    <Text as="span" fontSize="sm">Dry run, then a REAL live run if the clean-gate passes (HOLD email otherwise).</Text>
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {form.run_mode === 'auto_live' && (
              <Box borderWidth="1px" borderRadius="md" borderColor="orange.300" p={3}>
                <Alert status="warning" borderRadius="md" fontSize="xs" mb={3} py={2}>
                  <AlertIcon boxSize={4} />
                  auto_live writes real money to the real sheets. Every live interlock still applies:
                  live_enabled must be ON in Config, the unstamped-paid guard must be green, and only
                  one live run can execute at a time. A failed live run auto-pauses this schedule.
                </Alert>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Hold (skip live + email) when the dry run has any of these flags</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {HOLDABLE_FLAGS.map((flag) => (
                      <Checkbox
                        key={flag}
                        size="sm"
                        isChecked={form.hold_flags.includes(flag)}
                        onChange={(e) => set('hold_flags', e.target.checked
                          ? [...form.hold_flags, flag]
                          : form.hold_flags.filter((f) => f !== flag))}
                      >
                        <Text as="span" fontSize="xs" fontFamily="mono">{flag}</Text>
                      </Checkbox>
                    ))}
                  </Flex>
                </FormControl>
                <Flex gap={6} wrap="wrap" mb={3}>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <Switch size="sm" isChecked={form.hold_on_blank_payee}
                      onChange={(e) => set('hold_on_blank_payee', e.target.checked)} mr={2} />
                    <FormLabel fontSize="xs" mb={0}>Hold on blank payee amounts</FormLabel>
                  </FormControl>
                  <FormControl display="flex" alignItems="center" w="auto">
                    <Switch size="sm" isChecked={form.pause_on_failure}
                      onChange={(e) => set('pause_on_failure', e.target.checked)} mr={2} />
                    <FormLabel fontSize="xs" mb={0}>Auto-pause this schedule if a run fails</FormLabel>
                  </FormControl>
                </Flex>
                <FormControl maxW="260px">
                  <FormLabel fontSize="sm">Max total payout (optional cap)</FormLabel>
                  <NumberInput size="sm" min={0} value={form.max_total}
                    onChange={(v) => set('max_total', v)}>
                    <NumberInputField placeholder="no cap" />
                  </NumberInput>
                  <FormHelperText fontSize="xs">HOLD if the run&apos;s grand total exceeds this ($).</FormHelperText>
                </FormControl>
              </Box>
            )}

            <FormControl isRequired>
              <FormLabel fontSize="sm">Notify emails (comma-separated)</FormLabel>
              <Input size="sm" value={form.notify_emails}
                onChange={(e) => set('notify_emails', e.target.value)} />
              <FormHelperText fontSize="xs">
                Gets the dry-run report / HOLD / live-success / FAILED emails.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Notes</FormLabel>
              <Textarea size="sm" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <Switch size="sm" isChecked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} mr={2} />
              <FormLabel fontSize="sm" mb={0}>Enabled</FormLabel>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button colorScheme={form.run_mode === 'auto_live' ? 'orange' : 'purple'} onClick={save} isLoading={saving}>
            {isEdit ? 'Save changes' : 'Create schedule'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
